import { withErrorBoundary } from 'react-error-boundary';
import type { FilterOptionType } from '../../shared/types';
import type { SelectorButtonProps } from './types';
import { type HTMLAttributes, useCallback, useContext, useMemo, useState } from 'react';
import type IMultiSelectOption from 'components/shared/inputs/multiSelect/shared/types/multiSelectOption';
import MultiSelectWithConfirm from 'components/shared/inputs/multiSelect/MultiSelectWithConfirm';
import { useTranslation } from 'react-i18next';
import { Choose } from 'autogen/translation-keys/trans-website-shared-buttons';
import styled from 'styled-components';
import ArticleSearchResponseContext from 'components/pages/ArticleUniverse/components/Filter/context/ArticleSearchResponseContext';
import { getSelectTypeIds } from '../../helpers/isSelected';

const StyledMultiSelectWithConfirm = styled(MultiSelectWithConfirm)`
    min-width: 120px;
`;

function mapToOptions(selectOptions: FilterOptionType[]): IMultiSelectOption[] {
    return selectOptions.map((option) => ({
        value: option.queryParameterName,
        label: option.publicName
    }));
}

function getSelectedOptions(selectOptions: FilterOptionType[], selectedIds: string[]): IMultiSelectOption[] {
    const selectedOptions = selectOptions.filter((option) => selectedIds.includes(option.entryId));
    return mapToOptions(selectedOptions);
}

function MultiSelectFilter(props: SelectorButtonProps & HTMLAttributes<HTMLSelectElement>) {
    const { buttonLabel, selectOptions, selectType, toggleOptionToFilterArray, ...restProps } = props;
    const articleSearchResponseContext = useContext(ArticleSearchResponseContext);
    const selectedIds = getSelectTypeIds(selectType, articleSearchResponseContext?.data);
    const [selectedOptions, setSelectedOptions] = useState<IMultiSelectOption[]>(
        getSelectedOptions(selectOptions, selectedIds)
    );
    const options = useMemo(() => mapToOptions(selectOptions), [selectOptions]);
    const { t } = useTranslation();

    const handleSelection = useCallback(
        (options: IMultiSelectOption[]) => {
            toggleOptionToFilterArray({ queryParameterNames: options.map((item) => item.value), selectType });
            setSelectedOptions(options);
        },
        [toggleOptionToFilterArray, selectType]
    );

    return (
        <StyledMultiSelectWithConfirm
            confirmText={t(Choose)}
            triggerText={selectedOptions.length > 0 ? `${buttonLabel} (${selectedOptions.length})` : buttonLabel}
            value={selectedOptions}
            name={selectType}
            options={options}
            {...restProps}
            onChange={(selectedOptions) => {
                handleSelection(selectedOptions);
            }}
        />
    );
}

export default withErrorBoundary(MultiSelectFilter, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
